import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { Type } from 'fc-ui-components';

import { ImageResolution } from 'helpers';
import { UserService } from 'services/user.service';
import { WindowService } from 'services/window.service';
import { SegmentService } from 'services/segment.service';
import { ProductService } from 'app/content/services/product.service';
import { DownloadInitiator } from 'services/tracking/download-initiator';
import { BulkDownloadImagesInformation } from 'app/content/models/product.model';
import { HistoricalOrder } from 'app/order-management/services/order-history';
import { CsvLanguageOption } from '../manufacturer-platform/content/bulk-search/bulk-search.component';

@Injectable()
export class DownloadContentService {
    public order: HistoricalOrder;

    private downloadContentAlertSubject: Subject<DownloadContentAlert> = new Subject<DownloadContentAlert>();
    public downloadContentAlert: Observable<DownloadContentAlert> = this.downloadContentAlertSubject.asObservable();

    constructor(
        private productService: ProductService,
        private userService: UserService,
        private windowService: WindowService,
        private segmentService: SegmentService
    ) {}

    setOrder(order: HistoricalOrder) {
        this.order = order;
    }

    getOrderData(): { orderId: string; orderType: string } {
        return {
            orderId: this.order.orderId,
            orderType: this.order.type
        };
    }

    public downloadContent(
        downloadType: DownloadEventType,
        downloadValue: DownloadValue,
        downloadInitiator: DownloadInitiator,
        selectedResolution?: ImageResolution,
        selectedCsvLanguage?: string,
        images = true,
        video = false
    ): Observable<any> {
        let downloadObservable: Observable<any>;

        switch (downloadType) {
            case DownloadEventType.DOWNLOAD_CSV: {
                downloadObservable = this.productService.bulkDownload({
                    ...(downloadValue.gtins ? { gtins: downloadValue.gtins } : {}),
                    ...(downloadValue.articleNumbers ? { articleNumbers: downloadValue.articleNumbers } : {}),
                    ...(downloadValue.orderId ? { orderId: downloadValue.orderId } : {}),
                    ...(downloadValue.orderType ? { orderType: downloadValue.orderType } : {}),
                    userLanguage: this.userService.getPreferredLanguage(),
                    size: selectedResolution,
                    csvLanguage: selectedCsvLanguage,
                    initiator: downloadInitiator
                });
                this.trackOrderHistoryDownload({
                    order: this.order,
                    csvDownloadType: true,
                    imageDownloadType: false,
                    selectedLanguage:
                        selectedCsvLanguage === CsvLanguageOption.UserPreferredLanguage
                            ? this.userService.getPreferredLanguage()
                            : selectedCsvLanguage,
                    selectedImageQuality: selectedResolution
                });
                break;
            }
            case DownloadEventType.DOWNLOAD_IMAGES: {
                downloadObservable = this.productService.bulkImagesDownload({
                    ...(downloadValue.gtins ? { gtins: downloadValue.gtins } : {}),
                    ...(downloadValue.articleNumbers ? { articleNumbers: downloadValue.articleNumbers } : {}),
                    ...(downloadValue.orderId ? { orderId: downloadValue.orderId } : {}),
                    ...(downloadValue.orderType ? { orderType: downloadValue.orderType } : {}),
                    size: selectedResolution,
                    initiator: downloadInitiator,
                    images,
                    video
                });
                this.trackOrderHistoryDownload({
                    order: this.order,
                    csvDownloadType: false,
                    imageDownloadType: true,
                    selectedLanguage: selectedCsvLanguage,
                    selectedImageQuality: selectedResolution
                });
                break;
            }
            case DownloadEventType.DOWNLOAD_IMAGES_WITH_CSV: {
                downloadObservable = this.productService.bulkImagesDownloadWithCsv({
                    ...(downloadValue.gtins ? { gtins: downloadValue.gtins } : {}),
                    ...(downloadValue.articleNumbers ? { articleNumbers: downloadValue.articleNumbers } : {}),
                    ...(downloadValue.orderId ? { orderId: downloadValue.orderId } : {}),
                    ...(downloadValue.orderType ? { orderType: downloadValue.orderType } : {}),
                    userLanguage: this.userService.getPreferredLanguage(),
                    size: selectedResolution,
                    csvLanguage: selectedCsvLanguage,
                    initiator: downloadInitiator,
                    images,
                    video
                });
                this.trackOrderHistoryDownload({
                    order: this.order,
                    csvDownloadType: true,
                    imageDownloadType: true,
                    selectedLanguage: selectedCsvLanguage,
                    selectedImageQuality: selectedResolution
                });
                break;
            }
            default:
                break;
        }

        return downloadObservable;
    }

    public handleDownload(downloadObservable: Observable<any>): void {
        downloadObservable.subscribe(
            ({ downloadUrl, downloadInformation }) => {
                if (_.isEmpty(downloadUrl)) {
                    this.downloadContentAlertSubject.next({
                        display: true,
                        type: Type.Error,
                        message: 'ORDER_HISTORY.DOWNLOAD_CONTENT.ERROR'
                    });
                    return;
                }
                if (downloadInformation && downloadInformation.notFoundImages) {
                    this.downloadContentAlertSubject.next({
                        display: true,
                        type: Type.Info,
                        message: 'ORDER_HISTORY.DOWNLOAD_CONTENT.NO_IMAGES_FOUND',
                        downloadInformation: _.pick(downloadInformation, ['notFoundImages', 'requested'])
                    });
                }
                this.windowService.location = downloadUrl;
            },
            (error) => {
                this.downloadContentAlertSubject.next({
                    display: true,
                    type: Type.Error,
                    message: 'ORDER_HISTORY.DOWNLOAD_CONTENT.ERROR'
                });
            }
        );
    }

    private trackOrderHistoryDownload({
        order,
        selectedLanguage,
        csvDownloadType,
        imageDownloadType,
        selectedImageQuality
    }: {
        order: HistoricalOrder;
        selectedLanguage: string;
        csvDownloadType: boolean;
        imageDownloadType: boolean;
        selectedImageQuality: ImageResolution;
    }) {
        const { brands, orderNumber, productItems } = order;
        const brandsName = brands.map((brand) => brand.name);
        const gtinCount = productItems.map((productItem) => productItem.gtin).length;

        const trackingData = {
            orderNumber,
            brandsName,
            gtinCount,
            csvDownloadType,
            selectedLanguage,
            imageDownloadType,
            selectedImageQuality
        };
        this.segmentService.track('[CO] Order Downloaded', trackingData);
    }
}

export type DownloadValue = {
    gtins?: string[];
    articleNumbers?: string[];
    orderId?: string;
    orderType?: string;
};

export enum DownloadEventType {
    DOWNLOAD_CSV = 'DOWNLOAD_CSV',
    DOWNLOAD_IMAGES = 'DOWNLOAD_IMAGES',
    DOWNLOAD_IMAGES_WITH_CSV = 'DOWNLOAD_IMAGES_WITH_CSV'
}

export enum SearchType {
    ARTICLE_NUMBERS = 'article_numbers',
    GTINS = 'gtins',
    ORDER_ID = 'orderId'
}

export type DownloadContentAlert = {
    display: boolean;
    type?: Type;
    message?: string;
    downloadInformation?: Pick<BulkDownloadImagesInformation, 'notFoundImages' | 'requested'>;
};
